import React, { useMemo, useRef, useState } from 'react';
import { CommissionSchedule } from '../components/CommissionSchedule';
import { DashboardHeader } from '../components/DashboardHeader';
import { ExportingIndicator } from '../components/ExportingIndicator';
import { IncentiveTracker } from '../components/IncentiveTracker';
import { LoanBreakdownTable } from '../components/LoanBreakdownTable';
import { RoleSummaryCard } from '../components/RoleSummaryCard';
import { StatCard } from '../components/StatCard';
import { periods, producer } from '../data/commissions';
import { roles } from '../data/roles';
import { formatCurrency, payingStep as resolvePayingStep, sumVolume, totalCommission } from '../utils/commission';
import { exportElementToPdf } from '../utils/pdf';
import { buildLoans } from '../utils/production';
export function CommissionsDashboard() {
  const [selectedRoleId, setSelectedRoleId] = useState(roles[2].id);
  const [selectedPeriodId, setSelectedPeriodId] = useState(periods[0].id);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const statementRef = useRef<HTMLDivElement>(null);
  const role = roles.find(item => item.id === selectedRoleId) ?? roles[0];
  const period = periods.find(item => item.id === selectedPeriodId) ?? periods[0];
  const units = role.monthlyUnits[period.id] ?? 0;
  const loans = useMemo(() => buildLoans(period, role.id, units), [period, role.id, units]);
  const volume = sumVolume(loans);
  const scorecardMet = period.scorecard.points >= period.scorecard.goal;
  const payingStep = resolvePayingStep(role, units, scorecardMet);
  const activeStepIndex = role.steps.findIndex(step => step.minUnits === payingStep.minUnits);
  const totals = totalCommission(role, payingStep, loans);
  const projectedUnits = period.isCurrent ? Math.round(units * period.daysTotal / period.daysElapsed) : units;
  const projectedStep = resolvePayingStep(role, projectedUnits, scorecardMet);
  const projectedGross = role.model === 'bps' ? units > 0 ? volume / units * projectedUnits * (projectedStep.totalBps ?? 0) / 10000 : 0 : projectedUnits * (projectedStep.perUnit ?? 0);
  const projected = role.monthlyCap ? Math.min(projectedGross, role.monthlyCap) : projectedGross;
  const rateValue = role.model === 'bps' ? `${payingStep.totalBps} BPS` : formatCurrency(payingStep.perUnit ?? 0);
  const rateHelper = role.model === 'bps' ? payingStep.bonusBps ? `${role.baseBps} base + ${payingStep.bonusBps} bonus` : `${role.baseBps} base · no bonus` : payingStep.perUnit ? `per ${role.incentiveNoun ?? 'unit'}` : 'below the first incentive band';
  async function handleExport() {
    const node = statementRef.current;
    if (!node || isExporting) return;
    setExportError(null);
    setIsExporting(true);
    try {
      // Let the export-only header state paint before capturing.
      await new Promise(resolve => window.setTimeout(resolve, 80));
      await exportElementToPdf(node, `commission-statement-${role.id}-${period.id}.pdf`);
    } catch {
      setExportError('We couldn’t generate the PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }
  return <div className="min-h-screen w-full bg-slate-50" data-id="element-163" data-mp="1jwk00x27pjfu">
      <div ref={statementRef} className="bg-slate-50" data-id="element-164" data-mp="1jtxrov7hyzwk">
        <DashboardHeader producer={producer} role={role} roles={roles} onSelectRole={setSelectedRoleId} periods={periods} selectedPeriodId={period.id} onSelectPeriod={setSelectedPeriodId} onExport={handleExport} isExporting={isExporting} data-id="element-165" data-mp="m9kxi5ou9ha1" />

        <main className="mx-auto max-w-7xl space-y-6 px-6 py-8" data-id="element-166" data-mp="10zirx3dd337o">
          {exportError ? <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" data-id="element-167" data-mp="3fxvnvbrvuel">
              {exportError}
            </p> : null}

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-168" data-mp="257hjko1epwj8">
            <StatCard label={period.isCurrent ? 'Earned month to date' : 'Commission earned'} value={formatCurrency(totals.net)} helper={totals.capped ? `Capped at ${formatCurrency(role.monthlyCap ?? 0)}` : `${loans.length} ${role.unitNounPlural}`} emphasis data-id="element-169" data-mp="2554ulqx8elwo" />
            <StatCard label={`${role.scope} closed units`} value={String(units)} helper={`${formatCurrency(volume)} volume`} data-id="element-170" data-mp="2ox9q2j51q3hv" />
            <StatCard label={role.model === 'bps' ? 'Earning rate' : 'Incentive rate'} value={rateValue} helper={rateHelper} data-id="element-171" data-mp="2xweolpglxwu1" />
            <StatCard label={period.isCurrent ? 'Projected month end' : 'Final month total'} value={formatCurrency(projected)} helper={period.isCurrent ? `On pace for ${projectedUnits} ${role.unitNounPlural}` : 'Statement closed'} data-id="element-172" data-mp="18is04ta4svv5" />
          </div>

          <div className="grid items-stretch gap-6 xl:grid-cols-3" data-id="element-173" data-mp="35g63l5xo1m3o">
            <RoleSummaryCard role={role} units={units} activeStepIndex={Math.max(0, activeStepIndex)} data-id="element-174" data-mp="3aehdfgnynwlw" />
            <div className="xl:col-span-2" data-id="element-175" data-mp="2wept6y6upjzg">
              <IncentiveTracker role={role} units={units} volume={volume} scorecard={period.scorecard} payingStep={payingStep} scorecardMet={scorecardMet} isCurrent={period.isCurrent} data-id="element-176" data-mp="2wdwv0llyk2sx" />
            </div>
          </div>

          <LoanBreakdownTable key={`${role.id}-${period.id}`} role={role} loans={loans} payingStep={payingStep} periodLabel={period.label} hideSearch={isExporting} data-id="element-177" data-mp="2rhg3rijgp8bm" />

          <CommissionSchedule role={role} activeStepIndex={Math.max(0, activeStepIndex)} data-id="element-178" data-mp="o84iit1jv6m5" />
        </main>
      </div>

      {isExporting ? <ExportingIndicator label={`${role.name} · ${period.label}`} data-id="element-179" data-mp="ja2xi2kkcx81" /> : null}
    </div>;
}